/**
 * AppPreviewMock - Realistic app-like preview with interactive tabs
 * Shows Today, Leads, and Revenue Flow with realistic mock UI
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, MessageCircle, Phone, Clock, AlertTriangle, CheckCircle2, Smartphone, Zap, TrendingUp, DollarSign, Flame, Snowflake } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

type Tab = 'Today' | 'Leads' | 'Revenue Flow';

export function AppPreviewMock() {
  const [activeTab, setActiveTab] = useState<Tab>('Today');

  return (
    <div className="relative">
      {/* Window Container */}
      <div className="rounded-xl border border-border/40 bg-card shadow-2xl overflow-hidden">
        {/* Top Bar with Tabs */}
        <div className="flex items-center gap-1 px-4 py-2 bg-secondary/30 border-b border-border/40">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-warning/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-success/60" />
          </div>
          <div className="flex items-center gap-1 ml-4">
            {(['Today', 'Leads', 'Revenue Flow'] as Tab[]).map((tab) => {
              const isActive = activeTab === tab;
              return (
                <motion.button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded-md transition-all relative',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    isActive
                      ? 'bg-primary/10 text-primary border border-primary/20'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent/10'
                  )}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                >
                  {tab}
                  {isActive && (
                    <motion.div
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                      layoutId="activeTab"
                      initial={false}
                      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                    />
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Main Content Area with Tab Switching */}
        <div className="p-6 bg-background min-h-[400px] relative overflow-hidden">
          <AnimatePresence mode="wait">
            {activeTab === 'Today' && (
              <motion.div
                key="today"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="space-y-4"
              >
                {/* Priority Actions Card */}
                <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/[0.08] to-primary/[0.02] p-5 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />
                  
                  <div className="flex items-center justify-between mb-4 relative z-10">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-primary" />
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-primary">
                        Priority Actions
                      </h3>
                    </div>
                    <Badge variant="outline" className="text-xs px-2 py-0.5 bg-amber-500/10 border-amber-500/20 text-amber-500">
                      <Zap className="w-3 h-3 mr-1" />
                      Priority 92
                    </Badge>
                  </div>

                  <div className="mb-4 relative z-10">
                    <h4 className="text-base font-bold text-foreground mb-1">Adolfo Russo</h4>
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-sm font-semibold text-primary">€6.5K at risk</p>
                      <div className="h-1 w-1 rounded-full bg-muted-foreground/40" />
                      <p className="text-xs text-muted-foreground">Due in 15 min</p>
                    </div>
                    <p className="text-xs text-muted-foreground">Overdue follow-up • Never contacted</p>
                  </div>

                  <div className="grid grid-cols-2 gap-2 relative z-10">
                    <motion.button
                      type="button"
                      className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Smartphone className="w-3.5 h-3.5" />
                      Send to phone
                    </motion.button>
                    <motion.button
                      type="button"
                      className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-success/10 text-success border border-success/20 text-xs font-medium hover:bg-success/20 transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Done
                    </motion.button>
                    <motion.button
                      type="button"
                      className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-accent/10 text-foreground border border-border/40 text-xs font-medium hover:bg-accent/20 transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Phone className="w-3.5 h-3.5" />
                      Call
                    </motion.button>
                    <motion.button
                      type="button"
                      className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-accent/10 text-foreground border border-border/40 text-xs font-medium hover:bg-accent/20 transition-colors"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <MessageCircle className="w-3.5 h-3.5" />
                      WhatsApp
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'Leads' && (
              <motion.div
                key="leads"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="space-y-3"
              >
                {/* Lead List Snippet */}
                <div className="space-y-2">
                  {[
                    { name: 'Martina Rossi', value: '€9,000', temp: 'hot', score: 92 },
                    { name: 'Giovanni Bianchi', value: '€4,500', temp: 'warm', score: 68 },
                    { name: 'Sofia Verdi', value: '€2,200', temp: 'cold', score: 45 },
                  ].map((lead, i) => (
                    <motion.div
                      key={i}
                      className={cn(
                        'p-3 rounded-lg border cursor-pointer transition-all',
                        i === 0
                          ? 'border-primary/30 bg-primary/5'
                          : 'border-border/40 bg-card hover:border-border/60'
                      )}
                      whileHover={{ y: -1, borderColor: 'rgba(59, 130, 246, 0.3)' }}
                      transition={{ duration: 0.15 }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-foreground">{lead.name}</h4>
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-xs px-2 py-0.5',
                            lead.temp === 'hot' && 'bg-destructive/10 border-destructive/20 text-destructive',
                            lead.temp === 'warm' && 'bg-warning/10 border-warning/20 text-warning',
                            lead.temp === 'cold' && 'bg-primary/10 border-primary/20 text-primary'
                          )}
                        >
                          {lead.temp === 'hot' && <Flame className="w-3 h-3 mr-1" />}
                          {lead.temp === 'warm' && <Zap className="w-3 h-3 mr-1" />}
                          {lead.temp === 'cold' && <Snowflake className="w-3 h-3 mr-1" />}
                          {lead.temp.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{lead.value}</span>
                        <span className="text-primary font-semibold">Score: {lead.score}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Expanded Lead Panel */}
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-4 p-4 rounded-lg border border-primary/30 bg-primary/5"
                >
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-foreground">Martina Rossi</h4>
                    <Badge variant="outline" className="text-xs bg-destructive/10 border-destructive/20 text-destructive">
                      <Flame className="w-3 h-3 mr-1" />
                      HOT
                    </Badge>
                  </div>
                  <div className="space-y-2 text-xs text-muted-foreground">
                    <div className="flex items-center justify-between">
                      <span>Deal Value</span>
                      <span className="text-foreground font-semibold">€9,000</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Last Contact</span>
                      <span className="text-foreground">Never contacted</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Priority Score</span>
                      <span className="text-primary font-semibold">92</span>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}

            {activeTab === 'Revenue Flow' && (
              <motion.div
                key="revenue-flow"
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -12 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="space-y-3"
              >
                {/* Revenue at Risk Overview */}
                <div className="rounded-lg border border-border/40 bg-card p-4">
                  <div className="flex items-center gap-2 mb-4">
                    <AlertTriangle className="w-4 h-4 text-destructive" />
                    <h3 className="text-sm font-semibold text-foreground">Revenue at Risk</h3>
                  </div>
                  <div className="space-y-2">
                    {[
                      { name: 'Adolfo Russo', value: '€6.5K', risk: 'high', highlighted: true },
                      { name: 'Martina Rossi', value: '€9.0K', risk: 'high', highlighted: false },
                      { name: 'Giovanni Bianchi', value: '€4.5K', risk: 'medium', highlighted: false },
                    ].map((row, i) => (
                      <motion.div
                        key={i}
                        className={cn(
                          'p-3 rounded-lg border transition-all',
                          row.highlighted
                            ? 'border-destructive/30 bg-destructive/5'
                            : 'border-border/40 bg-accent/5 hover:border-border/60'
                        )}
                        whileHover={{ y: -1 }}
                        transition={{ duration: 0.15 }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div className={cn(
                              'w-2 h-2 rounded-full',
                              row.risk === 'high' ? 'bg-destructive' : 'bg-warning'
                            )} />
                            <span className="text-sm text-foreground">{row.name}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-semibold text-foreground">{row.value}</span>
                            {row.highlighted && (
                              <Badge variant="outline" className="text-xs bg-destructive/10 border-destructive/20 text-destructive">
                                Urgent
                              </Badge>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Caption */}
      <p className="mt-4 text-xs text-center text-muted-foreground/80 max-w-md mx-auto leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
        Every action is prioritized by the system. You execute. No decision needed.
      </p>
    </div>
  );
}
